import { useEffect, useMemo, useState } from 'react';
import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useDebounceValue } from 'usehooks-ts';
import throttle from 'lodash/throttle';
import { monolithApi } from '@/utils/api';
import { queryClient } from '@/utils/query';

const ERROR_STATUSES: Record<number, string | null> = {
  204: null,
  409: 'not_available',
  422: 'too_many_attempts',
  429: 'too_many_attempts',
} as const;

const RATE_LIMITED_STATUSES = [422, 429];

const DEBOUNCE_DELAY = 500; // 500ms
const THROTTLE_DELAY = 2000; // 2s

const getErrorFromStatus = (status?: number) =>
  status && ERROR_STATUSES[status] !== undefined ? ERROR_STATUSES[status] : null;

interface UsernameValidationResponse {
  status: number;
  retryAfter: number | null;
}

const fetchValidation = async (username: string): Promise<UsernameValidationResponse> => {
  const result = await monolithApi.post(
    '/users/validations/username',
    {
      user: {
        username,
      },
    },
    {
      validateStatus: (status) => status < 500,
    },
  );
  const retryAfter = result.headers['retry-after'];
  const retryAfterNumber = parseInt(retryAfter, 10);

  return {
    status: result.status,
    retryAfter: Number.isNaN(retryAfterNumber) ? null : retryAfterNumber,
  };
};

const useValidateUsernameQuery = (
  username: string,
  options?: Omit<UseQueryOptions<UsernameValidationResponse, AxiosError<undefined>>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery({
    queryKey: ['validate-user-name', username],
    queryFn: () => fetchValidation(username),
    ...options,
  });
};

interface UsernameValidation {
  validationError: string | null;
  isValid: boolean;
}

export const useValidateUsername = (username: string): UsernameValidation => {
  const [debouncedUsername] = useDebounceValue<string>(username, DEBOUNCE_DELAY);
  const [usernameToValidate, setUsernameToValidate] = useState<string>(debouncedUsername);
  const [isWaiting, setIsWaiting] = useState<boolean>(false);
  const updateUsername = useMemo(() => throttle(setUsernameToValidate, THROTTLE_DELAY), []);

  useEffect(() => {
    if (!isWaiting) {
      updateUsername(debouncedUsername);
    }
  }, [debouncedUsername, updateUsername, isWaiting]);

  const enabled = usernameToValidate.length > 1;

  const { data } = useValidateUsernameQuery(usernameToValidate, {
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 120000, // 2 minutes
  });

  useEffect(() => {
    if (data) {
      if (RATE_LIMITED_STATUSES.includes(data.status) && data.retryAfter !== null) {
        const retryMs = data.retryAfter * 1000;
        setIsWaiting(true);
        setTimeout(() => {
          setIsWaiting(false);
          queryClient.invalidateQueries({ queryKey: ['validate-user-name', debouncedUsername] });
        }, retryMs);
      }
    }
  }, [data, debouncedUsername]);

  const validationError = getErrorFromStatus(username !== usernameToValidate && !isWaiting ? undefined : data?.status);

  return {
    isValid: validationError === null,
    validationError,
  };
};
